import React, { useCallback } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';

import { expVal } from '@confluence/feature-experiments';

import { BulkCreateHeader } from '../bulk-work-create-common/BulkCreateHeader.tsx';
import { SidePanelWrapper } from '../work-create-common/SidePanelWrapper.tsx';
import { messages } from '../../messages.ts';
import { useManualBulkCreateState } from '../../utils/useManualBulkCreateState.tsx';
import { useResetManualBulkCreateState } from '../../providers/BulkCreateContextProvider.tsx';
import { useGetIssueInsertionActions } from '../../services/useGetIssueInsertionActions.tsx';
import { BULK_ISSUE_CREATE_SOURCE } from '../../utils/useBulkCreateLoggingEvents.tsx';
import { WorkItemCreationError } from '../work-create-common/index.tsx';

import { BulkCreateBody } from './BulkCreateBody.tsx';
import { BulkCreateButtonGroup } from './BulkCreateButtonGroup.tsx';

const contentStyles = xcss({
	width: '100%',
	height: '100%',
	display: 'flex',
	flexDirection: 'column',
	flexWrap: 'nowrap',
});

const footerBaseStyles = xcss({
	backgroundColor: 'elevation.surface',
	flexShrink: 0,
	position: 'relative',
	'::before': {
		content: '""',
		position: 'absolute',
		top: 'space.0',
		left: 'space.150',
		right: 'space.150',
		height: '1px',
		backgroundColor: 'elevation.surface',
		borderTopColor: 'color.border',
		borderTopStyle: 'solid',
		borderTopWidth: 'border.width',
	},
});

const footerTopStyles = xcss({
	paddingTop: 'space.150',
	paddingBottom: 'space.150',
	paddingLeft: 'space.200',
	paddingRight: 'space.200',
});

const loadingScreenStyles = xcss({
	width: '100%',
	height: '100%',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	textAlign: 'center',
});

export const BulkCreateDefaultView = ({ onClose }: { onClose: () => void }) => {
	const { isLoading, isError, isIdle } = useManualBulkCreateState();

	const resetManualBulkCreateState = useResetManualBulkCreateState();
	const { addSingleIssuesToPage } = useGetIssueInsertionActions();

	// TO-DO need to implement logic to retry fetching Jira site / project / issue type
	// const handleTryAgain = () => {}

	const onPanelClose = useCallback(() => {
		addSingleIssuesToPage();
		/*
			Once the confluence-bulk-work-create-editor experiment is enabled we'll be resetting
			the bulk create state in BulkCreateSidePanel's handleOnClose method instead of here.
			We should clean up this code when cleaning up the experiment.
		*/
		if (!expVal('confluence-bulk-work-create-editor', 'isEnabled', false)) {
			void resetManualBulkCreateState();
		}
		onClose();
	}, [resetManualBulkCreateState, addSingleIssuesToPage, onClose]);

	return (
		<SidePanelWrapper>
			<Stack xcss={contentStyles}>
				<BulkCreateHeader onClose={onPanelClose} />
				{isLoading && (
					<Box xcss={loadingScreenStyles}>
						<Spinner size="large" />
					</Box>
				)}
				{isError && (
					<WorkItemCreationError
						creationSource={BULK_ISSUE_CREATE_SOURCE}
						errorMessage={
							expVal('project-terminology-refresh', 'isEnabled', false)
								? messages.connectJiraProjectErrorDescriptionGalaxia
								: messages.connectJiraProjectErrorDescription
						}
					/>
				)}
				{isIdle && (
					<>
						<BulkCreateBody />
						<Box xcss={footerBaseStyles}>
							<Inline
								alignInline="center"
								grow="fill"
								spread="space-between"
								xcss={footerTopStyles}
							>
								<BulkCreateButtonGroup onClose={onPanelClose} onCreateAll={onClose} />
							</Inline>
						</Box>
					</>
				)}
			</Stack>
		</SidePanelWrapper>
	);
};
